import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import useApiCall from "../../hooks/ApiCall";
import { default_headers, getCookie, getHeaders } from "../../utils/api";
import { createSessionForExternal } from "../../utils/common";
import { setCrossDomainCookie } from "../../utils/cookie-utils";
import envConfig from "../../config";
import { useTheme } from "../../context/ThemeContext";
import { PageTitle } from "../CreateBusinessAccount/CreateBusinessAccount";
import { getAppTheme } from "../../config/resolve-app-brand";
import useApiCallDefault from "../../hooks/ApiCallDefault";
import ProgressModal from "../../components/ProgressModal/ProgressModal";

const CreateDemoBusinessFile = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isDarkMode } = useTheme();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const theme = getAppTheme(isDarkMode);
  const [isLoading, setIsLoading] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [demoReady, setDemoReady] = useState(false);

  const { data: subscribersData, loading: subscribersLoading } = useApiCall({
    url: `https://${
      envConfig.apiDev1
    }/api/v3/en-au/subscribers/get-selection-list?LoginEmail=${getCookie(
      "loginEmail"
    )}&BaseHostURL=${envConfig.loginUrl}`,
    method: "GET",
    headers: {},
  });

  const {
    data: businessTypesData,
    error: businessTypesError,
    loading: businessTypesLoading,
  } = useApiCallDefault(
    `https://${envConfig.apiDev1}/api/v3/en-au/businesses/get-types?BaseHostURL=${envConfig.loginUrl}`,
    "GET",
    default_headers,
    null,
    navigate
  );

  const {
    data: businessActivities,
    error: businessActivitiesError,
    loading: businessActivitiesLoading,
  } = useApiCallDefault(
    `https://${envConfig.apiDev1Exacc}/api/v1/en-au/business-details/get-business-main-activities-async`,
    "GET",
    default_headers,
    null,
    navigate
  );

  const formik = useFormik({
    initialValues: {
      subscriberAccount: null,
      businessType: 3, // Auto-set to Company
    },
    onSubmit: async (values) => {
      if (!executeRecaptcha) return;
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha("create_demo_file");
      } catch {
        enqueueSnackbar("reCAPTCHA verification failed. Please try again.", { variant: "error" });
        return;
      }
      setIsLoading(true);
      setShowProgressModal(true);
      setDemoReady(false);

      try {
        let subscriberId = values.subscriberAccount?.xxsId;

        if (!subscriberId) {
          const subscriberResponse = await fetch(
            `https://${envConfig.apiDev1}/api/v5/en-au/subscribers/create-subscriber-with-business-file-async?BaseHostURL=${envConfig.loginUrl}`,
            {
              method: "POST",
              headers: {
                ...getHeaders(),
                captchav3: envConfig.loginUrl + ":" + recaptchaToken,
              },

              body: JSON.stringify({
                BusinessName: "Demo Subscriber",
                BusinessTypeId: values.businessType,
                BusinessRegistered4GSTYN: "Y",
                AutoAccessYN: "Y",
                AllowTeamMembersInviteExternalUsersYN: "Y",
                CountryId: 14,
                OwnerEmail: getCookie("loginEmail"),
                TaxAgentRAN: "",
                TaxAgentName: "",
                TaxAgentEmail: "",
                TaxAgentPhone: "",
                DemoYN: "Y",
              }),
            }
          );
          const subscriberData = await subscriberResponse.json();
          console.log(subscriberData);
          if (subscriberData.SuccessYN !== "Y") {
            throw new Error(
              subscriberData.ErrorMessage || "Failed to create subscriber"
            );
          }
          if (subscriberData.SuccessYN === "Y") {
            setDemoReady(true);
            setCrossDomainCookie("databaseId", subscriberData.BusinessFileId);
            setIsLoading(false);
          }
          return;
        }

        const apiData = {
          CountryID: 14,
          BusinessTypeID: values.businessType,
          CompanyName: "Demo",
          DatabaseOwnerEmail: getCookie("loginEmail"),
          CreatedByGlobalUserID: getCookie("globalUserId"),
          DatabaseSubscriberID: subscriberId,
          BusinessActivityCode:
            businessActivities?.ListOfBusinessActivities?.[0]?.Value || 0,
          BusinessRegistered4GSTYN: "Y",
          BillingSubscriderID: getCookie("globalUserId"),
          CurrentSubscriberIsAdminYN: "Y",
          CurrentSubscriberAutoAddYN: "Y",
          CurrentSubscriberCanAddOthersYN: "Y",
          IsDemoFile: true,
          TaxAgentRAN: "",
          TaxAgentName: "",
          TaxAgentEmail: "",
          TaxAgentPhone: "",
        };

        // Check if current domain includes '.exx.'
        const useExaccApi = window.location.origin.includes(".exx.");
        const apiUrl = useExaccApi
          ? `https://${envConfig.apiDev1}/api/v6/en-au/databases/create-new-business-file-async?BaseHostURL=${envConfig.loginUrl}`
          : `https://${envConfig.apiDev1}/api/v6/en-au/databases/create-new-business-file-async?BaseHostURL=${envConfig.loginUrl}`;

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            ...getHeaders(),
            captchav3: envConfig.loginUrl + ":" + recaptchaToken,
          },
          body: JSON.stringify(apiData),
        });

        const responseData = await response.json();

        if (responseData.SuccessYN === "Y") {
          setDemoReady(true);
          setCrossDomainCookie("databaseId", responseData.NewBusinessFileId);
          setIsLoading(false);
        } else {
        }
      } catch (error) {
        setIsLoading(false);
        setShowProgressModal(false);
      }
    },
  });

  const handleCloseModal = () => {
    setShowProgressModal(false);
    setDemoReady(false);
  };

  const handleStartExperience = async () => {
    const databaseId = getCookie("databaseId");
    if (databaseId) {
      await createSessionForExternal(
        `https://${envConfig.mainServiceUrl}/${databaseId}/dashboard`
      );
    }
  };

  if (subscribersLoading) return <CircularProgress />;

  return (
    <Box sx={{ mb: 8 }}>
      <PageTitle
        title="Demo Business File"
        onBack={() => navigate("/user-dashboard")}
        displayBackIcon={true}
      />
      <Box
        sx={{
          boxShadow: "rgba(69, 90, 100, 0.1) 0px 0px 10px 0px",
          px: 5,
          pt: 6,
          pb: 10.25,
          border: theme.palette.borders.main,
          borderRadius: "8px",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            fontSize: "32px",
            fontWeight: "bold",
            color: theme.palette.primary.main,
          }}
        >
          Demo Business File
        </Typography>
        <Typography sx={{ mb: 4 }}>
          Get your dedicated demo environment with access to all corners of
          Exacc and packed with sample data. Invite your team to evaluate
          together.
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          {subscribersData?.ListOfSubscribers?.length > 0 && (
            <Autocomplete
              sx={{ mb: 4, maxWidth: "480px" }}
              autoSelect
              autoHighlight
              options={subscribersData?.ListOfSubscribers || []}
              getOptionLabel={(option) => option?.xxsName || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={subscribersData?.ListOfSubscribers?.length > 0}
                  label="Subscriber account"
                  fullWidth
                  data-testid="create-demo-business-file--TextField-0" />
              )}
              value={formik.values.subscriberAccount}
              onChange={(_, value) =>
                formik.setFieldValue("subscriberAccount", value)
              }
              data-testid="create-demo-business-file--Autocomplete-0" />
          )}
          <Autocomplete
            sx={{ mb: 4, maxWidth: "480px", display: "none" }}
            required
            options={businessTypesData?.ListOfBusinessTypes || []}
            getOptionLabel={(option) => option?.xxtTypeName || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Business Type"
                fullWidth
                data-testid="create-demo-business-file--TextField-1" />
            )}
            value={
              businessTypesData?.ListOfBusinessTypes?.find(
                (type) => type.xxtID === formik.values.businessType
              ) || null
            }
            onChange={(_, value) =>
              formik.setFieldValue("businessType", value?.xxtID)
            }
            data-testid="create-demo-business-file--Autocomplete-1" />

          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                isLoading ||
                (!formik.values.subscriberAccount &&
                  subscribersData?.ListOfSubscribers?.length > 0)
              }
              data-testid="create-demo-business-file--Button-0">
              {isLoading ? <CircularProgress size={24} /> : "Get Demo"}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate("/user-dashboard")}
              data-testid="create-demo-business-file--Button-1">
              Cancel
            </Button>
          </Box>
        </form>
      </Box>
      <ProgressModal
        open={showProgressModal}
        demoReady={demoReady}
        onClose={handleCloseModal}
        onStartExperience={handleStartExperience}
      />
    </Box>
  );
};

export default CreateDemoBusinessFile;
